import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  api,
  enabledStacks,
  fetchProviderAccountProfile,
  hasActiveProviderProfile,
  ProviderProfession,
} from '../api'
import Spinner from '../components/Spinner'
import { auth } from '../firebase'
import ClientProfileForm from '../components/ClientProfileForm'
import RegisterDoctor from './register/RegisterDoctor'
import RegisterEducation from './register/RegisterEducation'
import RegisterProperty from './register/RegisterProperty'
import RegisterTech from './register/RegisterTech'
import RegisterFinancial from './register/RegisterFinancial'
import RegisterAllied from './register/RegisterAllied'
import RegisterMigration from './register/RegisterMigration'
import RegisterLegal from './register/RegisterLegal'
import { RatingStarsDisplay } from '../components/RatingStars'
import StripeIdentityVerifyPanel from '../components/StripeIdentityVerifyPanel'
import { useAccountAvatar } from '../contexts/AccountAvatarContext'
import { detectTimeZone, normalizeCountryCode } from '../countryRules'
import '../styles.css'

type Props = {
  profile?: any
  onLogout?: () => void
}

const initials = (name?: string) =>
  name
    ? name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() || '')
        .join('')
    : '—'

const CONTACT_SLOT = 'REQUEST_BOOKING'

const parseReviewDate = (value?: string | null) => {
  if (!value) return null
  const trimmed = String(value).trim()
  if (!trimmed || trimmed === CONTACT_SLOT) return null
  if (/^\d+$/.test(trimmed)) {
    const raw = Number(trimmed)
    const ms = trimmed.length <= 10 ? raw * 1000 : raw
    const parsed = new Date(ms)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsed = new Date(trimmed)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const sortReviewsByDate = (reviews: any[]) =>
  [...reviews].sort((a, b) => {
    const aDate = parseReviewDate(a?.slotISO) || parseReviewDate(a?.createdAt)
    const bDate = parseReviewDate(b?.slotISO) || parseReviewDate(b?.createdAt)
    const aTime = aDate ? aDate.getTime() : 0
    const bTime = bDate ? bDate.getTime() : 0
    return bTime - aTime
  })

const fmtReviewDate = (slotISO?: string | null, createdAt?: string | null) => {
  const parsed = parseReviewDate(slotISO) || parseReviewDate(createdAt)
  if (!parsed) return ''
  return parsed.toLocaleDateString('en-AU', {
    timeZone: detectTimeZone(),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const PROVIDER_STACKS: Array<{
  id: ProviderProfession
  label: string
  registerPath: string
  fetchProfile: () => Promise<any>
}> = [
  {
    id: 'doctor',
    label: 'Medical Doctors',
    registerPath: '/professionals/doctors',
    fetchProfile: () => fetchProviderAccountProfile('doctor'),
  },
  {
    id: 'property',
    label: 'Property, Design & Building',
    registerPath: '/professionals/property',
    fetchProfile: () => fetchProviderAccountProfile('property'),
  },
  {
    id: 'tech',
    label: 'Tech, Creative & Media',
    registerPath: '/professionals/tech',
    fetchProfile: () => fetchProviderAccountProfile('tech'),
  },
  {
    id: 'financial',
    label: 'Financial Advisory',
    registerPath: '/professionals/financial',
    fetchProfile: () => fetchProviderAccountProfile('financial'),
  },
  {
    id: 'allied',
    label: 'Allied Health',
    registerPath: '/professionals/allied',
    fetchProfile: () => fetchProviderAccountProfile('allied'),
  },
  {
    id: 'legal',
    label: 'Legal, Governance & Compliance',
    registerPath: '/professionals/legal',
    fetchProfile: () => fetchProviderAccountProfile('legal'),
  },
  {
    id: 'migration',
    label: 'Migration & Visa',
    registerPath: '/professionals/migration',
    fetchProfile: () => fetchProviderAccountProfile('migration'),
  },
  {
    id: 'education',
    label: 'Education & Tutoring',
    registerPath: '/professionals/education',
    fetchProfile: () => fetchProviderAccountProfile('education'),
  },
]
const ENABLED_PROVIDER_STACKS = PROVIDER_STACKS.filter((stack) => enabledStacks.includes(stack.id))

const MAX_AVATAR_BYTES = 5 * 1024 * 1024

async function uploadToS3(presign: { url: string; fields: Record<string, string> }, file: File) {
  const form = new FormData()
  Object.entries(presign.fields || {}).forEach(([k, v]) => {
    form.append(k, v)
  })
  form.append('file', file)
  const res = await fetch(presign.url, { method: 'POST', body: form })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Upload failed')
  }
}

export default function AccountPage({ profile, onLogout }: Props) {
  const { setAccountAvatarUrl } = useAccountAvatar()
  const [clientProfile, setClientProfile] = React.useState<any>(profile)
  const [doctorProfile, setDoctorProfile] = React.useState<any>(null)
  const [providerProfiles, setProviderProfiles] = React.useState<Record<string, any>>({})
  const [stackStatuses, setStackStatuses] = React.useState<
    Record<string, 'unknown' | 'approved' | 'unapproved'>
  >({})
  const [activeProviderStack, setActiveProviderStack] = React.useState<ProviderProfession>('doctor')
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [clientFormOpen, setClientFormOpen] = React.useState(false)
  const [doctorFormOpen, setDoctorFormOpen] = React.useState(false)
  const [propertyFormOpen, setPropertyFormOpen] = React.useState(false)
  const [techFormOpen, setTechFormOpen] = React.useState(false)
  const [financialFormOpen, setFinancialFormOpen] = React.useState(false)
  const [educationFormOpen, setEducationFormOpen] = React.useState(false)
  const [alliedFormOpen, setAlliedFormOpen] = React.useState(false)
  const [legalFormOpen, setLegalFormOpen] = React.useState(false)
  const [migrationFormOpen, setMigrationFormOpen] = React.useState(false)
  const [clientRatingSummary, setClientRatingSummary] = React.useState<{ ratingAverage?: number; ratingCount?: number } | null>(null)
  const [doctorReviews, setDoctorReviews] = React.useState<{ summary?: { ratingAverage?: number; ratingCount?: number }; reviews?: any[] } | null>(null)
  const [doctorReviewsLoading, setDoctorReviewsLoading] = React.useState(false)
  const [avatarUploading, setAvatarUploading] = React.useState(false)
  const [avatarMessage, setAvatarMessage] = React.useState<string | null>(null)
  const location = useLocation()
  const clientReviewRef = React.useRef<HTMLDivElement | null>(null)
  const providerReviewRef = React.useRef<HTMLDivElement | null>(null)
  const avatarInputRef = React.useRef<HTMLInputElement | null>(null)
  const avatarMessageTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const fetchedStacks = React.useRef<Set<ProviderProfession>>(new Set())
  const activeProviderProfile = (() => {
    const p = providerProfiles[activeProviderStack]
    if (!p || p.needsRegistration) return null
    return p
  })()
  const educationProfile = providerProfiles.education || null
  const propertyProfile = providerProfiles.property || null
  const techProfile = providerProfiles.tech || null
  const financialProfile = providerProfiles.financial || null
  const alliedProfile = providerProfiles.allied || null
  const legalProfile = providerProfiles.legal || null
  const migrationProfile = providerProfiles.migration || null
  const activeProviderStacks = React.useMemo(
    () =>
      ENABLED_PROVIDER_STACKS.filter(
        (stack) => {
          const profile = providerProfiles[stack.id]
          const hasLoadedProfile = Boolean(profile && !profile.needsRegistration)
          return hasLoadedProfile || stackStatuses[stack.id] !== 'unapproved'
        }
      ),
    [providerProfiles, stackStatuses]
  )
  const activeProviderMeta =
    activeProviderStacks.find((stack) => stack.id === activeProviderStack) || activeProviderStacks[0]

  const loadStackProfile = React.useCallback(
    async (stackId: ProviderProfession) => {
      if (fetchedStacks.current.has(stackId)) return
      fetchedStacks.current.add(stackId)
      const meta = ENABLED_PROVIDER_STACKS.find((s) => s.id === stackId)
      if (!meta) return
      try {
        const profile = await meta.fetchProfile().catch(() => null)
        if (profile) {
          setProviderProfiles((prev) => ({ ...prev, [stackId]: profile }))
          if (stackId === 'doctor') setDoctorProfile(profile)
          setStackStatuses((prev) => ({
            ...prev,
            [stackId]: hasActiveProviderProfile(profile) ? 'approved' : 'unapproved',
          }))
        } else {
          setProviderProfiles((prev) => ({ ...prev, [stackId]: { needsRegistration: true } }))
          setStackStatuses((prev) => ({ ...prev, [stackId]: 'unapproved' }))
        }
      } catch {
        setProviderProfiles((prev) => ({ ...prev, [stackId]: { needsRegistration: true } }))
        setStackStatuses((prev) => ({ ...prev, [stackId]: 'unapproved' }))
      }
    },
    []
  )

  React.useEffect(() => {
    let cancelled = false

    const loadClient = async () => {
      setLoading(true)
      setError(null)
      const attempts = 2
      for (let i = 0; i < attempts; i++) {
        try {
          const clientRes = await api.me()
          if (cancelled) return
          setClientProfile(clientRes)
          setError(null)
          return
        } catch (e: any) {
          if (cancelled) return
          const isLastTry = i === attempts - 1
          if (isLastTry) {
            setError(e?.message || 'Failed to load account')
          } else {
            await new Promise((res) => setTimeout(res, 500))
          }
        }
      }
    }

    loadClient().finally(() => {
      if (!cancelled) setLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => {
    if (activeProviderStacks.length === 0) {
      const fallback =
        (ENABLED_PROVIDER_STACKS.find((s) => stackStatuses[s.id] !== 'unapproved')?.id as
          ProviderProfession) || 'doctor'
      setActiveProviderStack(fallback)
      return
    }
    const fallbackStack = activeProviderStacks[0]?.id || 'doctor'
    setActiveProviderStack((prev) =>
      activeProviderStacks.some((s) => s.id === prev) ? prev : fallbackStack
    )
  }, [activeProviderStacks, stackStatuses])

  React.useEffect(() => {
    if (!activeProviderMeta) return
    loadStackProfile(activeProviderMeta.id)
  }, [activeProviderMeta, loadStackProfile])

  React.useEffect(() => {
    // Ensure we fetch the active stack once if nothing is approved yet.
    loadStackProfile(activeProviderStack)
  }, [activeProviderStack, loadStackProfile])

  React.useEffect(() => {
    const clientId = clientProfile?.userId || clientProfile?.user_id
    if (!clientId) {
      setClientRatingSummary(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const summary = await api.getClientRatingSummary(clientId)
        if (!cancelled) setClientRatingSummary(summary)
      } catch {
        if (!cancelled) setClientRatingSummary(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [clientProfile?.userId, clientProfile?.user_id])

  React.useEffect(() => {
    const specialistId = doctorProfile?.userId || doctorProfile?.specialistId
    if (!specialistId) {
      setDoctorReviews(null)
      return
    }
    let cancelled = false
    setDoctorReviewsLoading(true)
    ;(async () => {
      try {
        const res = await api.getSpecialistReviews(specialistId)
        if (!cancelled) setDoctorReviews(res)
      } catch {
        if (!cancelled) setDoctorReviews(null)
      } finally {
        if (!cancelled) setDoctorReviewsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [doctorProfile?.userId, doctorProfile?.specialistId])

  React.useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('section') === 'reviews') {
      const target =
        params.get('role') === 'provider' ? providerReviewRef.current : clientReviewRef.current
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [location.search])

  const firstName =
    clientProfile?.firstName ||
    clientProfile?.name?.split(' ')?.[0] ||
    'there'

  const displayName =
    clientProfile?.name ||
    `${clientProfile?.firstName || ''} ${clientProfile?.lastName || ''}`.trim()
  const avatarInitials = displayName ? initials(displayName) : '—'
  const avatarUrl = clientProfile?.avatarUrl || activeProviderProfile?.photoUrl || null
  const pendingSpecialistPhoto = activeProviderProfile?.pendingPhoto || null
  const activeProviderCountryCode = normalizeCountryCode(
    activeProviderProfile?.address?.countryCode ||
      activeProviderProfile?.address?.country ||
      clientProfile?.address?.countryCode ||
      clientProfile?.address?.country ||
      'AU'
  )

  React.useEffect(() => {
    setAccountAvatarUrl(avatarUrl || null)
  }, [avatarUrl, setAccountAvatarUrl])

  const needsRegistration = clientProfile?.needsRegistration ?? false
  const refreshClientProfile = React.useCallback(async () => {
    try {
      const me = await api.me()
      setClientProfile(me)
    } catch (e) {
      console.error(e)
    }
  }, [])
const refreshDoctorProfile = React.useCallback(async () => {
  try {
    const doc = await fetchProviderAccountProfile('doctor')
    setDoctorProfile(doc)
    setProviderProfiles(prev => ({ ...prev, doctor: doc }))
  } catch (e) {
    console.error(e)
  }
}, [])
const refreshPropertyProfile = React.useCallback(async () => {
  try {
    const property = await fetchProviderAccountProfile('property')
    setProviderProfiles(prev => ({ ...prev, property }))
  } catch (e) {
    console.error(e)
  }
}, [])
const refreshTechProfile = React.useCallback(async () => {
  try {
    const tech = await fetchProviderAccountProfile('tech')
    setProviderProfiles(prev => ({ ...prev, tech }))
  } catch (e) {
    console.error(e)
  }
}, [])
const refreshFinancialProfile = React.useCallback(async () => {
  try {
    const fin = await fetchProviderAccountProfile('financial')
    setProviderProfiles(prev => ({ ...prev, financial: fin }))
  } catch (e) {
    console.error(e)
  }
}, [])
const refreshEducationProfile = React.useCallback(async () => {
  try {
    const edu = await fetchProviderAccountProfile('education')
    setProviderProfiles(prev => ({ ...prev, education: edu }))
  } catch (e) {
    console.error(e)
  }
}, [])
const refreshAlliedProfile = React.useCallback(async () => {
  try {
    const allied = await fetchProviderAccountProfile('allied')
    setProviderProfiles(prev => ({ ...prev, allied }))
  } catch (e) {
    console.error(e)
  }
}, [])
const refreshLegalProfile = React.useCallback(async () => {
  try {
    const legal = await fetchProviderAccountProfile('legal')
    setProviderProfiles(prev => ({ ...prev, legal }))
  } catch (e) {
    console.error(e)
  }
}, [])
const refreshMigrationProfile = React.useCallback(async () => {
  try {
    const migration = await fetchProviderAccountProfile('migration')
    setProviderProfiles(prev => ({ ...prev, migration }))
  } catch (e) {
    console.error(e)
  }
}, [])
  const handleDoctorEditSuccess = React.useCallback(async () => {
    setDoctorProfile(prev => prev ? { ...prev, approvalStatus: 'pending' } : prev)
    await refreshDoctorProfile()
    setDoctorFormOpen(false)
  }, [refreshDoctorProfile])
  const handlePropertyEditSuccess = React.useCallback(async () => {
    await refreshPropertyProfile()
    setPropertyFormOpen(false)
  }, [refreshPropertyProfile])
  const handleTechEditSuccess = React.useCallback(async () => {
    await refreshTechProfile()
    setTechFormOpen(false)
  }, [refreshTechProfile])
  const handleFinancialEditSuccess = React.useCallback(async () => {
    await refreshFinancialProfile()
    setFinancialFormOpen(false)
  }, [refreshFinancialProfile])
  const handleEducationEditSuccess = React.useCallback(async () => {
    await refreshEducationProfile()
    setEducationFormOpen(false)
  }, [refreshEducationProfile])
  const handleAlliedEditSuccess = React.useCallback(async () => {
    await refreshAlliedProfile()
    setAlliedFormOpen(false)
  }, [refreshAlliedProfile])
  const handleLegalEditSuccess = React.useCallback(async () => {
    await refreshLegalProfile()
    setLegalFormOpen(false)
  }, [refreshLegalProfile])
  const handleMigrationEditSuccess = React.useCallback(async () => {
    await refreshMigrationProfile()
    setMigrationFormOpen(false)
  }, [refreshMigrationProfile])

  React.useEffect(() => {
    if (!doctorFormOpen) return
    refreshDoctorProfile()
  }, [doctorFormOpen, refreshDoctorProfile])
  React.useEffect(() => {
    if (!propertyFormOpen) return
    refreshPropertyProfile()
  }, [propertyFormOpen, refreshPropertyProfile])
  React.useEffect(() => {
    if (!techFormOpen) return
    refreshTechProfile()
  }, [techFormOpen, refreshTechProfile])
  React.useEffect(() => {
    if (!financialFormOpen) return
    refreshFinancialProfile()
  }, [financialFormOpen, refreshFinancialProfile])
  React.useEffect(() => {
    if (!educationFormOpen) return
    refreshEducationProfile()
  }, [educationFormOpen, refreshEducationProfile])
  React.useEffect(() => {
    if (!alliedFormOpen) return
    refreshAlliedProfile()
  }, [alliedFormOpen, refreshAlliedProfile])
  React.useEffect(() => {
    if (!legalFormOpen) return
    refreshLegalProfile()
  }, [legalFormOpen, refreshLegalProfile])
  React.useEffect(() => {
    if (!migrationFormOpen) return
    refreshMigrationProfile()
  }, [migrationFormOpen, refreshMigrationProfile])
  const doctorEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Important:</strong> Updating your doctor profile pauses your listing until the EUCA team re-approves it.
      </p>
      <p style={{ margin: 0 }}>If you need to submit new documents, email contact@eucamatch.com.</p>
    </div>
  )
  const propertyEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Heads up:</strong> Updating your property profile pauses your listing until the EUCA team re-approves it.
      </p>
      <p style={{ margin: 0 }}>Need to refresh your licence or credentials? Email contact@eucamatch.com with your documents.</p>
    </div>
  )
  const techEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Heads up:</strong> Updating your tech & creative profile pauses your listing until the EUCA team re-approves it.
      </p>
      <p style={{ margin: 0 }}>Need to refresh credentials? Email contact@eucamatch.com with your documents.</p>
    </div>
  )
  const financialEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Heads up:</strong> Updating your financial advisor profile pauses your listing until the EUCA team re-approves it.
      </p>
      <p style={{ margin: 0 }}>Need to refresh credentials? Email contact@eucamatch.com with your documents.</p>
    </div>
  )
  const educationEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Heads up:</strong> Any educator profile changes are reviewed manually before going live.
      </p>
      <p style={{ margin: 0 }}>Need to update credentials? Email contact@eucamatch.com with your documents.</p>
    </div>
  )
  const alliedEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Heads up:</strong> Updating Allied Health details pauses your listing until the EUCA team re-approves it.
      </p>
      <p style={{ margin: 0 }}>Need to refresh credentials? Email contact@eucamatch.com with your latest documents.</p>
    </div>
  )
  const legalEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Heads up:</strong> Updating Legal, Governance & Compliance details pauses your listing until they’re re-approved.
      </p>
      <p style={{ margin: 0 }}>Need to refresh your certifications? Email contact@eucamatch.com with supporting documents.</p>
    </div>
  )
  const migrationEditNotice = (
    <div>
      <p style={{ marginBottom: 8 }}>
        <strong>Heads up:</strong> Updating Migration & Visa details pauses your listing until the EUCA team re-approves it.
      </p>
      <p style={{ margin: 0 }}>Need to refresh your MARA or visa credentials? Email contact@eucamatch.com with your documents.</p>
    </div>
  )

  const clientInitial = React.useMemo(() => {
    if (!clientProfile) return undefined
    const names = (clientProfile.firstName && clientProfile.lastName)
      ? [clientProfile.firstName, clientProfile.lastName]
      : (clientProfile.name || '').split(' ')
    const first = clientProfile.firstName || names[0] || ''
    const last = clientProfile.lastName || names.slice(1).join(' ') || ''
    return {
      firstName: first,
      lastName: last,
      state: clientProfile.address?.state || '',
      postalCode: clientProfile.address?.postalCode || '',
      phone: clientProfile.phone || '',
      altPhone: clientProfile.altPhone || '',
      email: clientProfile.email || '',
    }
  }, [clientProfile])

  const handleLogout = () => {
    onLogout?.()
  }

  React.useEffect(() => {
    return () => {
      if (avatarMessageTimer.current) {
        clearTimeout(avatarMessageTimer.current)
      }
    }
  }, [])

  const handleAvatarClick = React.useCallback(() => {
    if (avatarUploading) return
    avatarInputRef.current?.click()
  }, [avatarUploading])

  const handleAvatarSelected = React.useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file) return
      if (!file.type.startsWith('image/')) {
        setAvatarMessage('Upload a PNG or JPG.')
        return
      }
      if (file.size > MAX_AVATAR_BYTES) {
        setAvatarMessage('Image must be 5 MB or smaller.')
        return
      }
      try {
        setAvatarUploading(true)
        setAvatarMessage(null)
        const presign = await api.presignUpload('avatar', file.type || 'image/jpeg')
        await uploadToS3(presign, file)
        const key = presign?.key || presign?.fields?.key
        if (!key) throw new Error('Upload failed—missing key')
        const res = await api.updateAvatar({ key })
        await refreshClientProfile()
        let autoClear = true
        try {
          const doctorRes = await api.updateDoctorPhoto({ key })
          await refreshDoctorProfile()
          if (doctorRes?.pending) {
            setAvatarMessage('Photo submitted for review')
            autoClear = false
          }
        } catch (err) {
          console.warn('Doctor photo sync skipped', err)
        }
        if (!doctorProfile || !autoClear) {
          if (!doctorProfile) setAvatarMessage(res?.avatarUrl ? 'Photo updated' : 'Photo saved')
        }
        if (autoClear) {
          if (avatarMessageTimer.current) clearTimeout(avatarMessageTimer.current)
          avatarMessageTimer.current = setTimeout(() => setAvatarMessage(null), 2500)
        }
      } catch (err: any) {
        setAvatarMessage(err?.message || 'Failed to update photo.')
      } finally {
        setAvatarUploading(false)
      }
    },
    [refreshClientProfile, doctorProfile, refreshDoctorProfile]
  )

  const providerReviewList = React.useMemo(() => {
    const list = Array.isArray(doctorReviews?.reviews) ? doctorReviews.reviews : []
    return sortReviewsByDate(list)
  }, [doctorReviews])

  const renderDoctorCard = (profile: any) => {
    const docStatus = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = docStatus === 'approved'
    const specialties = profile.specialty || (Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '')
    const pendingPhoto = profile.pendingPhoto
    return (
      <div className="account-provider-card">
        {pendingPhoto && (
          <div className="banner warning" style={{ marginBottom: '1rem' }}>
            Pending review — we’ll email you once the new photo is live.
          </div>
        )}
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img className="provider-photo" src={profile.photoUrl} alt={`${profile.name || 'Specialist'} headshot`} loading="lazy" />
            )}
            <div>
              <div className="provider-title">Medical Doctors</div>
              <div className="hint">Status: {docStatus ? docStatus.charAt(0).toUpperCase() + docStatus.slice(1) : 'Pending'}</div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialty</div>
            <div>{specialties || '—'}</div>
          </div>
          <div>
            <div className="field-label">Registration</div>
            <div>{profile.ahpraNumber || '—'}</div>
          </div>
          <div>
            <div className="field-label">Email</div>
            <div>{profile.email || profile.contactEmail || clientProfile?.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setDoctorFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <div className="account-provider-reviews" ref={providerReviewRef}>
          <div className="detail-label">Provider reviews</div>
          <div className="rating-summary" style={{ marginBottom: '1rem' }}>
            <RatingStarsDisplay value={doctorReviews?.summary?.ratingAverage || 0} showValue />
            <p className="hint">
              {doctorReviews?.summary?.ratingCount
                ? `${doctorReviews.summary.ratingCount} ${doctorReviews.summary.ratingCount === 1 ? 'review' : 'reviews'}`
                : 'No reviews yet'}
            </p>
          </div>
          {doctorReviewsLoading && (
            <div style={{ marginBottom: '1rem' }}>
              <Spinner label="Loading reviews…" />
            </div>
          )}
          {!doctorReviewsLoading && (
            <>
              {providerReviewList.length ? (
                <ul className="review-list">
                  {providerReviewList.map((rev: any) => (
                    <li key={rev.reviewId || `${rev.selectionId}-${rev.createdAt || ''}`}>
                      <div className="review-list-head">
                        <RatingStarsDisplay value={rev.rating || 0} />
                        <span className="hint">
                          {rev.reviewerFirstName || 'Client'} · {fmtReviewDate(rev.slotISO, rev.createdAt)}
                          {rev.source ? (
                            <>
                              {' - '}
                              <em>{rev.source}</em>
                            </>
                          ) : null}
                        </span>
                      </div>
                      {rev.comment ? (
                        <p className="hint" style={{ marginTop: 8 }}>
                          “{rev.comment}”
                        </p>
                      ) : (
                        <p className="hint" style={{ marginTop: 8 }}>No comment provided.</p>
                      )}
                      {rev.providerReply?.comment ? (
                        <div className="review-provider-reply">
                          <p className="hint" style={{ margin: 0, fontWeight: 600 }}>
                            {rev.providerReply?.replierName || 'Professional'} replied
                          </p>
                          <p className="hint" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>
                            “{rev.providerReply.comment}”
                          </p>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="hint">No public reviews yet.</p>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  const renderPropertyCard = (profile: any) => {
    const status = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = status === 'approved'
    const specialties = Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '—'
    const registration =
      profile.registrationNumber || profile.providerRegistrationNumber || profile.teacherRegistrationNumber || '—'
    return (
      <div className="account-provider-card">
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img className="provider-photo" src={profile.photoUrl} alt={`${profile.name || 'Property specialist'} headshot`} loading="lazy" />
            )}
            <div>
              <div className="provider-title">Property, Design & Building</div>
              <div className="hint">Status: {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}</div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialties</div>
            <div>{specialties}</div>
          </div>
          <div>
            <div className="field-label">Licence / registration</div>
            <div>{registration}</div>
          </div>
          <div>
            <div className="field-label">Public email</div>
            <div>{profile.publicEmail || profile.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setPropertyFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <p className="hint" style={{ marginTop: '1rem' }}>
          Reviews for property specialists are coming soon.
        </p>
      </div>
    )
  }

  const renderTechCard = (profile: any) => {
    const status = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = status === 'approved'
    const specialties = Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '—'
    const registration =
      profile.registrationNumber || profile.providerRegistrationNumber || profile.teacherRegistrationNumber || '—'
    return (
      <div className="account-provider-card">
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img className="provider-photo" src={profile.photoUrl} alt={`${profile.name || 'Tech, Creative & Media specialist'} headshot`} loading="lazy" />
            )}
            <div>
              <div className="provider-title">Tech, Creative & Media</div>
              <div className="hint">Status: {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}</div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialties</div>
            <div>{specialties}</div>
          </div>
          <div>
            <div className="field-label">Registration / licence</div>
            <div>{registration}</div>
          </div>
          <div>
            <div className="field-label">Public email</div>
            <div>{profile.publicEmail || profile.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setTechFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <p className="hint" style={{ marginTop: '1rem' }}>
          Reviews for tech & creative specialists are coming soon.
        </p>
      </div>
    )
  }

  const renderFinancialCard = (profile: any) => {
    const status = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = status === 'approved'
    const specialties = Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '—'
    const registration =
      profile.registrationNumber || profile.providerRegistrationNumber || profile.teacherRegistrationNumber || '—'
    return (
      <div className="account-provider-card">
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img className="provider-photo" src={profile.photoUrl} alt={`${profile.name || 'Financial advisor'} headshot`} loading="lazy" />
            )}
            <div>
              <div className="provider-title">Financial Advisory</div>
              <div className="hint">Status: {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}</div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialties</div>
            <div>{specialties}</div>
          </div>
          <div>
            <div className="field-label">Registration / licence</div>
            <div>{registration}</div>
          </div>
          <div>
            <div className="field-label">Public email</div>
            <div>{profile.publicEmail || profile.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setFinancialFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <p className="hint" style={{ marginTop: '1rem' }}>
          Reviews for financial advisors are coming soon.
        </p>
      </div>
    )
  }
  const renderEducationCard = (profile: any) => {
    const status = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = status === 'approved'
    return (
      <div className="account-provider-card">
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img className="provider-photo" src={profile.photoUrl} alt={`${profile.name || 'Educator'} headshot`} loading="lazy" />
            )}
            <div>
              <div className="provider-title">Education & Tutoring</div>
              <div className="hint">Status: {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}</div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialties</div>
            <div>{Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '—'}</div>
          </div>
          <div>
            <div className="field-label">Teaching registration</div>
            <div>{profile.teacherRegistrationNumber || '—'}</div>
          </div>
          <div>
            <div className="field-label">Public email</div>
            <div>{profile.publicEmail || profile.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setEducationFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <p className="hint" style={{ marginTop: '1rem' }}>
          Ratings for educators are coming soon.
        </p>
      </div>
    )
  }
  const renderAlliedCard = (profile: any) => {
    const status = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = status === 'approved'
    const specialties = Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '—'
    const registration =
      profile.registrationNumber || profile.providerRegistrationNumber || profile.teacherRegistrationNumber || '—'
    return (
      <div className="account-provider-card">
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img
                className="provider-photo"
                src={profile.photoUrl}
                alt={`${profile.name || 'Allied Health provider'} headshot`}
                loading="lazy"
              />
            )}
            <div>
              <div className="provider-title">Allied Health</div>
              <div className="hint">Status: {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}</div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialties</div>
            <div>{specialties || '—'}</div>
          </div>
          <div>
            <div className="field-label">Registration / provider number</div>
            <div>{registration || '—'}</div>
          </div>
          <div>
            <div className="field-label">Public email</div>
            <div>{profile.publicEmail || profile.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setAlliedFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <p className="hint" style={{ marginTop: '1rem' }}>
          Allied Health reviews appear per booking on the Appointments page.
        </p>
      </div>
    )
  }

  const renderLegalCard = (profile: any) => {
    const status = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = status === 'approved'
    const registration =
      profile.registrationNumber ||
      profile.providerRegistrationNumber ||
      profile.practiceRegistrationNumber ||
      profile.licenseNumber ||
      '—'
    const specialties = Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '—'
    return (
      <div className="account-provider-card">
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img
                className="provider-photo"
                src={profile.photoUrl}
                alt={`${profile.name || 'Legal specialist'} headshot`}
                loading="lazy"
              />
            )}
            <div>
              <div className="provider-title">Legal, Governance & Compliance</div>
              <div className="hint">
                Status: {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
              </div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialties</div>
            <div>{specialties || '—'}</div>
          </div>
          <div>
            <div className="field-label">Practising certificate / licence</div>
            <div>{registration || '—'}</div>
          </div>
          <div>
            <div className="field-label">Public email</div>
            <div>{profile.publicEmail || profile.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setLegalFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <p className="hint" style={{ marginTop: '1rem' }}>
          Legal reviews appear per booking on the Appointments page.
        </p>
      </div>
    )
  }

  const renderMigrationCard = (profile: any) => {
    const status = (profile.approvalStatus ?? profile.status ?? '').toLowerCase()
    const isApproved = status === 'approved'
    const registration =
      profile.agentRegistrationNumber ||
      profile.registrationNumber ||
      profile.providerRegistrationNumber ||
      '—'
    const specialties = Array.isArray(profile.specialties) ? profile.specialties.join(', ') : '—'
    return (
      <div className="account-provider-card">
        <div className="provider-head">
          <div className="provider-info">
            {profile.photoUrl && (
              <img
                className="provider-photo"
                src={profile.photoUrl}
                alt={`${profile.name || 'Migration specialist'} headshot`}
                loading="lazy"
              />
            )}
            <div>
              <div className="provider-title">Migration & Visa</div>
              <div className="hint">
                Status: {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Pending'}
              </div>
            </div>
          </div>
          <span className={`status-pill ${isApproved ? 'status-accepted' : 'status-pending'}`}>
            {isApproved ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="account-grid">
          <div>
            <div className="field-label">Specialties</div>
            <div>{specialties || '—'}</div>
          </div>
          <div>
            <div className="field-label">Migration agent / MARA number</div>
            <div>{registration || '—'}</div>
          </div>
          <div>
            <div className="field-label">Public email</div>
            <div>{profile.publicEmail || profile.email || '—'}</div>
          </div>
        </div>
        <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button className="btn" onClick={() => setMigrationFormOpen(true)}>
            View / edit details
          </button>
        </div>
        <p className="hint" style={{ marginTop: '1rem' }}>
          Migration reviews appear after each confirmed appointment.
        </p>
      </div>
    )
  }

  const renderActiveProviderCard = () => {
    if (!activeProviderProfile) return null
    if (activeProviderStack === 'doctor') return renderDoctorCard(activeProviderProfile)
    if (activeProviderStack === 'financial') return renderFinancialCard(activeProviderProfile)
    if (activeProviderStack === 'tech') return renderTechCard(activeProviderProfile)
    if (activeProviderStack === 'property') return renderPropertyCard(activeProviderProfile)
    if (activeProviderStack === 'education') return renderEducationCard(activeProviderProfile)
    if (activeProviderStack === 'allied') return renderAlliedCard(activeProviderProfile)
    if (activeProviderStack === 'legal') return renderLegalCard(activeProviderProfile)
    if (activeProviderStack === 'migration') return renderMigrationCard(activeProviderProfile)
    return null
  }

  return (
    <div className="container account-page">
      {loading && (
        <div style={{ padding: '1rem 0' }}>
          <Spinner label="Loading account…" />
        </div>
      )}
      {error && <div className="banner error">{error}</div>}

      {!loading && (
        <>
          <section className="card account-hero">
            <div className="account-avatar-shell">
              <button
                type="button"
                className={`account-avatar account-avatar-button ${avatarUrl ? 'has-photo' : ''} ${
                  avatarUploading ? 'is-uploading' : ''
                }`}
                onClick={handleAvatarClick}
                aria-label={avatarUploading ? 'Uploading photo' : 'Change profile photo'}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt={`${firstName}'s profile`} loading="lazy" />
                ) : (
                  avatarInitials
                )}
                <span className="avatar-overlay">
                  {avatarUploading ? 'Uploading…' : 'Change photo'}
                </span>
              </button>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                ref={avatarInputRef}
                style={{ display: 'none' }}
                onChange={handleAvatarSelected}
              />
              {pendingSpecialistPhoto ? (
                <p className="account-avatar-note">Pending review</p>
              ) : avatarMessage ? (
                <p className="account-avatar-note">{avatarMessage}</p>
              ) : null}
            </div>
            <div>
              <p className="account-kicker">Account</p>
              <h1>Hi, {firstName}.</h1>
              <p className="hint">{clientProfile?.email}</p>
            </div>
          </section>

          <section className="card account-section">
            <header>
              <div>
                <h3>Client profile</h3>
                <p className="hint">Shared across every profession.</p>
              </div>
              <span className={`status-pill ${needsRegistration ? 'status-pending' : 'status-accepted'}`}>
                {needsRegistration ? 'Incomplete' : 'Complete'}
              </span>
            </header>
            <div className="account-grid">
              <div>
                <div className="field-label">Name</div>
                <div>{clientProfile?.name || `${clientProfile?.firstName || ''} ${clientProfile?.lastName || ''}`.trim() || '—'}</div>
              </div>
              <div>
                <div className="field-label">Email</div>
                <div>{clientProfile?.email || '—'}</div>
              </div>
              <div>
                <div className="field-label">State</div>
                <div>{clientProfile?.address?.state || '—'}</div>
              </div>
              <div>
                <div className="field-label">Phone</div>
                <div>{clientProfile?.phone || '—'}</div>
              </div>
            </div>
            <div className="inline-gap-2" style={{ marginTop: '1rem' }}>
              {needsRegistration ? (
                <Link className="btn" to="/register/client">
                  Complete details
                </Link>
              ) : (
                <button className="btn" onClick={() => setClientFormOpen(true)}>
                  View / edit details
                </button>
              )}
            </div>
            {clientProfile && (
              <div ref={clientReviewRef} style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
                  <h4 style={{ margin: 0 }}>Client rating</h4>
                  <p className="hint" style={{ margin: 0 }}>
                    Specialists see this rating when reviewing your requests.
                  </p>
                </div>
                <div className="rating-summary" style={{ marginTop: 8 }}>
                  <RatingStarsDisplay value={clientRatingSummary?.ratingAverage || 0} showValue />
                  <p className="hint">
                    {clientRatingSummary?.ratingCount
                      ? `${clientRatingSummary.ratingCount} ${
                          clientRatingSummary.ratingCount === 1 ? 'review' : 'reviews'
                        }`
                      : 'No reviews yet'}
                  </p>
                </div>
              </div>
            )}
          </section>

          <section className="card account-section">
            <header>
              <div>
                <h3>Provider profiles</h3>
                <p className="hint">Each profession is verified separately.</p>
              </div>
            </header>
            {activeProviderStacks.length === 0 ? (
              <div className="banner info">
                <p style={{ margin: 0 }}>
                  No approved provider profiles yet. Complete a registration to list your services.
                </p>
              </div>
            ) : (
              <>
                <div className="chip-row" role="tablist" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
                  {activeProviderStacks.map(stack => (
                    <button
                      key={stack.id}
                      type="button"
                      className={`chip ${activeProviderStack === stack.id ? 'active' : ''}`}
                      onClick={() => setActiveProviderStack(stack.id)}
                    >
                      {stack.label}
                    </button>
                  ))}
                </div>
                {activeProviderProfile ? (
                  <>
                    {renderActiveProviderCard()}
                    <div className="account-provider-card" style={{ marginTop: '1rem' }}>
                      <div className="provider-head">
                        <div>
                          <div className="provider-title">Stripe ID verification</div>
                          <p className="hint" style={{ margin: '6px 0 0' }}>
                            Optional: complete Stripe verification to add an ID verified badge to your professional profile.
                          </p>
                        </div>
                      </div>
                      <StripeIdentityVerifyPanel
                        enabled={Boolean(auth.currentUser)}
                        countryCode={activeProviderCountryCode}
                        returnTo="/account"
                        mode="account"
                      />
                      <p className="hint" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                        Fee includes Stripe verification and processing fees.
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="banner info">
                    <p style={{ marginBottom: 8 }}>
                      You haven’t created a {activeProviderMeta?.label?.toLowerCase() || 'provider'} profile yet.
                    </p>
                    {activeProviderMeta?.registerPath && (
                      <Link className="btn" to={activeProviderMeta.registerPath}>
                        Start registration
                      </Link>
                    )}
                  </div>
                )}
              </>
            )}
          </section>

          <section className="card account-section">
            <h3>Security</h3>
            <p className="hint">You’re signed in on this device.</p>
            <button className="btn btn-outline" onClick={handleLogout}>
              Sign out
            </button>
          </section>
        </>
      )}

      {clientFormOpen && (
        <div className="overlay show" onClick={() => setClientFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, margin: '10vh auto' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit client profile</div>
              <button className="btn close" onClick={() => setClientFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body">
              <ClientProfileForm
                mode="edit"
                initial={clientInitial}
                onSuccess={async () => {
                  await refreshClientProfile()
                  setClientFormOpen(false)
                }}
              />
            </div>
          </div>
        </div>
      )}

      {doctorFormOpen && (
        <div className="overlay show" onClick={() => setDoctorFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit doctor profile</div>
              <button className="btn close" onClick={() => setDoctorFormOpen(false)}>✕</button>
            </div>
              <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <RegisterDoctor
                  key={doctorProfile?.updatedAt || doctorProfile?.status || doctorProfile?.name || 'register-doctor'}
                  user={auth.currentUser}
                  variant="inline"
                  inlineNotice={doctorEditNotice}
                  onSubmitSuccess={handleDoctorEditSuccess}
                  initialProfile={doctorProfile}
                />
            </div>
          </div>
        </div>
      )}

      {propertyFormOpen && (
        <div className="overlay show" onClick={() => setPropertyFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit real estate & property profile</div>
              <button className="btn close" onClick={() => setPropertyFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <RegisterProperty
                key={propertyProfile?.updatedAt || propertyProfile?.status || propertyProfile?.name || 'register-property'}
                user={auth.currentUser}
                variant="inline"
                inlineNotice={propertyEditNotice}
                onSubmitSuccess={handlePropertyEditSuccess}
                initialProfile={propertyProfile}
              />
            </div>
          </div>
        </div>
      )}

      {techFormOpen && (
        <div className="overlay show" onClick={() => setTechFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit tech & creative profile</div>
              <button className="btn close" onClick={() => setTechFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <RegisterTech
                key={techProfile?.updatedAt || techProfile?.status || techProfile?.name || 'register-tech'}
                user={auth.currentUser}
                variant="inline"
                inlineNotice={techEditNotice}
                onSubmitSuccess={handleTechEditSuccess}
                initialProfile={techProfile}
              />
            </div>
          </div>
        </div>
      )}

      {financialFormOpen && (
        <div className="overlay show" onClick={() => setFinancialFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit financial advisor profile</div>
              <button className="btn close" onClick={() => setFinancialFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <RegisterFinancial
                key={financialProfile?.updatedAt || financialProfile?.status || financialProfile?.name || 'register-financial'}
                user={auth.currentUser}
                variant="inline"
                inlineNotice={financialEditNotice}
                onSubmitSuccess={handleFinancialEditSuccess}
                initialProfile={financialProfile}
              />
            </div>
          </div>
        </div>
      )}

      {educationFormOpen && (
        <div className="overlay show" onClick={() => setEducationFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit education profile</div>
              <button className="btn close" onClick={() => setEducationFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <RegisterEducation
                key={educationProfile?.updatedAt || educationProfile?.status || educationProfile?.name || 'register-education'}
                user={auth.currentUser}
                variant="inline"
                inlineNotice={educationEditNotice}
                onSubmitSuccess={handleEducationEditSuccess}
                initialProfile={educationProfile}
              />
            </div>
          </div>
        </div>
      )}

      {alliedFormOpen && (
        <div className="overlay show" onClick={() => setAlliedFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit Allied Health profile</div>
              <button className="btn close" onClick={() => setAlliedFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <RegisterAllied
                key={alliedProfile?.updatedAt || alliedProfile?.status || alliedProfile?.name || 'register-allied'}
                user={auth.currentUser}
                variant="inline"
                inlineNotice={alliedEditNotice}
                onSubmitSuccess={handleAlliedEditSuccess}
                initialProfile={alliedProfile}
              />
            </div>
          </div>
        </div>
      )}

      {legalFormOpen && (
        <div className="overlay show" onClick={() => setLegalFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit Legal, Governance & Compliance profile</div>
              <button className="btn close" onClick={() => setLegalFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <RegisterLegal
                key={legalProfile?.updatedAt || legalProfile?.status || legalProfile?.name || 'register-legal'}
                user={auth.currentUser}
                variant="inline"
                inlineNotice={legalEditNotice}
                onSubmitSuccess={handleLegalEditSuccess}
                initialProfile={legalProfile}
              />
            </div>
          </div>
        </div>
      )}

      {migrationFormOpen && (
        <div className="overlay show" onClick={() => setMigrationFormOpen(false)}>
          <div className="sheet" onClick={e => e.stopPropagation()} style={{ maxWidth: 860, margin: '5vh auto', maxHeight: '90vh' }}>
            <div className="sheet-header">
              <div className="sheet-title">Edit Migration & Visa profile</div>
              <button className="btn close" onClick={() => setMigrationFormOpen(false)}>✕</button>
            </div>
            <div className="sheet-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <RegisterMigration
                key={migrationProfile?.updatedAt || migrationProfile?.status || migrationProfile?.name || 'register-migration'}
                user={auth.currentUser}
                variant="inline"
                inlineNotice={migrationEditNotice}
                onSubmitSuccess={handleMigrationEditSuccess}
                initialProfile={migrationProfile}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
